import { waitForElem } from '../../utils/wait_for_elem';

/**
  * searchAuthors
  *
  * Fuzzy-finder to progressively
  * hide authors on the main authors index.
  * Also the callback for clicking a letter to
  * "Browse by Last Name" (below the search filter).
  */
const searchAuthors = (e?: Event) => {
  const isVisible = (el: HTMLElement) => {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  revealAuthors(); // Should be pretty instantaneous, necessary to reveal more on backspace
  // Get filters
  const keywordElems = Array.from(document.getElementsByClassName('author-search-box'));
  let keyword = '';
  for (const kw of keywordElems) {
    if (isVisible(kw as HTMLElement)) {
      keyword = (kw as HTMLInputElement)?.value.toUpperCase() || '';
    }
  }
  const letterElems = Array.from(document.getElementsByClassName('letter-filter'));
  let letter = '';
  for (const l of letterElems) {
    if (isVisible(l as HTMLElement)) {
      letter = ((l?.querySelector('input:checked') as HTMLInputElement)?.value?.toUpperCase()) || '';
    }
  }
  const affiliationElems = Array.from(document.querySelectorAll('form.filter-affiliation'));
  let affiliation = '';
  for (const aff of affiliationElems) {
    if (isVisible(aff as HTMLElement)) {
      affiliation = ((aff?.querySelector('input:checked') as HTMLInputElement)?.value) || '';
    }
  }
  // If search activated from input
  if (e) {
    if ((e.currentTarget as HTMLElement).classList.contains('filter-letter')) {
      // Filter by this letter
      letter = ((e.currentTarget as HTMLElement).parentNode.querySelectorAll('input[name="last_name_first_letter"]')[0] as HTMLInputElement).value;
    }
    if ('key' in e) {
      if ((e.key === 'Escape' || e.key === 'Backspace') && keyword === '') {
        revealAuthors(true);  // Reset last name first letter filter
      }
    }
  }

  // Author Rows
  const authors = Array.from(document.getElementsByClassName('author-row'));
  // Letter Groups
  const letter_groups = document.getElementsByClassName('author-row-target');

  /* WARN: "data-<attribute>-" must be present in the template for retrieval */
  for (const a of authors) { // For each author row...
    const author = a.getElementsByTagName("a")[0];
    const author_first_name = author.dataset.first_name?.toUpperCase();
    const author_last_name = author.dataset.last_name?.toUpperCase();
    const author_organization = author.dataset.organization?.toUpperCase() ?? '';
    const author_is_staff = author.dataset.is_staff;

    /* affiliation - Filter by affiliation - "current" or "all" */
    if (affiliation.length) {
      switch (affiliation) {
        case 'current':
          if (author_is_staff === "True") {
            (a as HTMLElement).style.display = "";
          } else {
            (a as HTMLElement).style.display = "none";
          }
          break;

        case 'all':
          (a as HTMLElement).style.display = "";
          break;
      }
    }

    /* letter - Filter by last_name first letter */
    if (letter.length) {
      if (author_last_name.startsWith(letter) || author_last_name.startsWith(letter.toLowerCase())) {
        if ((a as HTMLElement).style.display !== "none") {
          (a as HTMLElement).style.display = "";
        }
      } else {
        (a as HTMLElement).style.display = "none";
      }
    }

    /* keyword - Filter by keyword */
    if (keyword.length) {
      const anch = a as HTMLElement;
      if (anch.style.display !== "none") {
        if (author_first_name.startsWith(keyword) || author_last_name.startsWith(keyword) || author_organization.indexOf(keyword) >= 0) {
          anch.style.display = "";
        } else {
          anch.style.display = "none";
        }
      }
    }
  }

  /** Handle letter group section visibility:
    * If the letter group is empty, hide the headline. */
  for (const letter_group of Array.from(letter_groups)) {
    /* Get all group children */
    const group_children = Array.from(letter_group.querySelectorAll('.author-row'));
    let has_visible_children = false;
    /* If letter group has visible children */
    for (const child of group_children) {
      if ((child as HTMLElement).style.display !== "none") { // is hidden
        has_visible_children = true;
      }
    }
    /* Reveal letter groups with visible children */
    if (has_visible_children === true) {
      (letter_group as HTMLElement).style.display = "";
    } else {
      (letter_group as HTMLElement).style.display = "none";
    }
  }
}

/**
  * revealAuthors
  *
  * Display all authors in the AuthorIndexPage list
  * by removing any "display: none" inline styles.
  */
const revealAuthors = (clear: boolean=false) => {
  /* Reveal all author rows. */
  const authors = Array.from(document.getElementsByClassName('author-row'));
  for (const a of authors) {
    (a as HTMLElement).style.display = "";
  }
  /* Reveal all letter groups. */
  const letter_groups = document.getElementsByClassName('author-row-target');
  for (const lg of Array.from(letter_groups)) {
    (lg as HTMLElement).style.display = "";
  }

  /** Clear the active last name first letter filter
    * and reset the keyword filter */
  if (clear) {
    document.querySelectorAll('input[name="last_name_first_letter"]')?.forEach(letter => {
      (letter as HTMLInputElement).checked = false;
    });
    document.querySelectorAll('input[name="keyword"]')?.forEach(input => {
      (input as HTMLInputElement).value = '';
    });
    searchAuthors();
  }
}

/** This function runs immediately,
  * continues if AuthorIndexPage filters
  * are found in the DOM.
  * These will setup the "Filter by":
  *   - Keyword
  *   - Letter (first letter of last name)
  *   - Affiliation (SEI or all) */
// Run on page load
waitForElem('.author-row-target').then(() => {
  /* Setup event listeners for each filter group. */
  /*  WARN: These classes must be present in the template for filtering to work */
  const letterFilters = document.getElementsByClassName('filter-letter');
  /** Filter by first letter of last name
    * There is one for mobile and one for desktop */
  if (letterFilters.length) {
    /** Add an onclick event listener to each of
      * the letter buttons in the "Browse by Last Name"
      * filter. */
    for (const letter of Array.from(letterFilters)) {
      letter.addEventListener('click', (e: MouseEvent) => {
        searchAuthors(e);
      });
    }
  }
  /** These classes must be present in the template for filtering to work */
  const searchInputs = document.getElementsByClassName('author-search-box');
  /** Filter by keyword
    * There is one setup for mobile, and one for desktop */
  if (searchInputs.length) {
    /* Add keyup event listener to the input filter */
    for (const input of Array.from(searchInputs)) {
      input.addEventListener('keyup', (e: KeyboardEvent) => {
        searchAuthors(e);
      });
    }
  }
  /** These classes must be present in the template for filtering to work */
  const affiliationForms = document.getElementsByClassName('filter-affiliation');
  /** Filter by affiliation
    * There are multiple inputs setup for mobile and desktop */
  if (affiliationForms.length) {
    /* Set initial filter state to only show current SEI employees by default */
    /* Add change event listener to inputs */
    for (const affForm of Array.from(affiliationForms)) {
      affForm.addEventListener('change', (e: Event) => {
        searchAuthors(e);
      });
    }
  }
  /** These classes must be present in the template for filtering to work */
  const clearButtons = document.getElementsByClassName('clear-button');
  if (clearButtons.length) {
    for (const button of Array.from(clearButtons)) {
      button.addEventListener('click', (e: Event) => {
        e.preventDefault();
        revealAuthors(true);
      });
    }
  }

  window.addEventListener('resize', () => {
    searchAuthors();
  });
});
